import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Eye, EyeOff, KeyRound, AlertTriangle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";

export default function ResetPasswordPage() {
    const { lang }   = useI18n();
    const navigate   = useNavigate();
    const token      = new URLSearchParams(window.location.search).get("token") || "";

    const [password,  setPassword]  = useState("");
    const [confirm,   setConfirm]   = useState("");
    const [showPw,    setShowPw]    = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done,      setDone]      = useState(false);
    const [error,     setError]     = useState("");

    const mismatch = confirm && password !== confirm;

    const submit = async (e) => {
        e.preventDefault();
        if (mismatch) return;
        setError("");
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate("/login"), 3000);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                (lang === "ar" ? "حدث خطأ. يرجى المحاولة لاحقًا." : "Something went wrong. Please try again.")
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <div className="w-full max-w-md surface-card p-8 text-center">
                    <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-4" />
                    <h2 className="text-lg font-semibold text-foreground mb-2">
                        {lang === "ar" ? "رابط غير صالح" : "Invalid Reset Link"}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-5">
                        {lang === "ar"
                            ? "هذا الرابط غير صالح أو منتهي الصلاحية."
                            : "This reset link is invalid or has expired."}
                    </p>
                    <Link
                        to="/forgot-password"
                        className="text-sm font-medium text-primary hover:underline"
                    >
                        {lang === "ar" ? "طلب رابط جديد" : "Request a new link"}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="w-full max-w-md">

                <div className="flex justify-center mb-8">
                    <img src="/hemaia_logo.svg" alt="Hemaia" className="h-10" />
                </div>

                <div className="surface-card p-8">
                    {done ? (
                        <div className="text-center py-4">
                            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold text-foreground mb-2">
                                {lang === "ar" ? "تم تغيير كلمة المرور!" : "Password Updated!"}
                            </h2>
                            <p className="text-sm text-muted-foreground mb-4">
                                {lang === "ar"
                                    ? "سيتم توجيهك لصفحة تسجيل الدخول خلال ثوانٍ..."
                                    : "Redirecting you to sign in..."}
                            </p>
                            <Link to="/login" className="text-sm font-medium text-primary hover:underline">
                                {lang === "ar" ? "تسجيل الدخول الآن" : "Sign in now"}
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <KeyRound className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-semibold text-foreground">
                                        {lang === "ar" ? "تعيين كلمة مرور جديدة" : "Set New Password"}
                                    </h1>
                                    <p className="text-sm text-muted-foreground">
                                        {lang === "ar" ? "اختر كلمة مرور قوية" : "Choose a strong password"}
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        {lang === "ar" ? "كلمة المرور الجديدة" : "New password"}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPw ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={6}
                                            autoFocus
                                            className="w-full px-3 py-2.5 pe-10 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPw((p) => !p)}
                                            className="absolute inset-y-0 end-0 flex items-center pe-3 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {lang === "ar" ? "٦ أحرف على الأقل" : "At least 6 characters"}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        {lang === "ar" ? "تأكيد كلمة المرور" : "Confirm password"}
                                    </label>
                                    <input
                                        type={showPw ? "text" : "password"}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        className={`w-full px-3 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${mismatch ? "border-destructive" : "border-border"}`}
                                    />
                                    {mismatch && (
                                        <p className="mt-1 text-xs text-destructive">
                                            {lang === "ar" ? "كلمتا المرور غير متطابقتين" : "Passwords do not match"}
                                        </p>
                                    )}
                                </div>

                                {error && (
                                    <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                                        {error}
                                        {error.toLowerCase().includes("expired") && (
                                            <span> <Link to="/forgot-password" className="underline font-medium">
                                                {lang === "ar" ? "طلب رابط جديد" : "Request a new link"}
                                            </Link>.</span>
                                        )}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting || !password || !confirm || !!mismatch}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {lang === "ar" ? "تحديث كلمة المرور" : "Update Password"}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
